import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation, Navigate } from 'react-router-dom';
import VoordeurOptionsPanel from '../components/VoordeurOptionsPanel';
import DoorDrawingBoard from '../components/DoorDrawingBoard';
import Summary from '../components/Summary';
import Quote from '../components/Quote';
import SavedQuotes from '../components/SavedQuotes';
import { useTranslation } from '../context/TranslationContext';
import { 
  glassTextures, 
  glazingTypes, 
  colors, 
  PRICING 
} from '../data/mockData';

const Voordeur = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [showSummary, setShowSummary] = useState(true);
  const [showQuote, setShowQuote] = useState(false);
  const [showSavedQuotes, setShowSavedQuotes] = useState(false);
  const drawingBoardRef = useRef(null);
  
  const [config, setConfig] = useState({
    width: 1000,
    height: 2000,
    doorStyle: 'style-1',
    designType: 'no-panel',
    doorType: 'normal',
    numberOfParts: 3,
    thresholdHeight: '30mm',
    handlePosition: 'left',
    parapet: 'standard-knob',
    security: 'none',
    glassTexture: 'helder-glas',
    glazingType: 'double',
    interiorColor: 'white',
    exteriorColor: 'anthracite',
    quantity: 1,
    overheadParts: 1,
    overheadPartWindowTypes: ['fixed-sash'],
    overheadHeight: undefined,
    leftPanelWidth: undefined,
    rightPanelWidth: undefined,
    leftPanelWindowType: 'fixed-sash',
    rightPanelWindowType: 'fixed-sash',
    leftPanelParapet: false, // Left panel parapet
    rightPanelParapet: false, // Right panel parapet
    leftPanelParapetHeight: 250, // Left panel parapet height in mm
    rightPanelParapetHeight: 250, // Right panel parapet height in mm
    leftPanelParapetColor: undefined, // Left panel parapet color
    rightPanelParapetColor: undefined, // Right panel parapet color
    leftPanelParapetPanelType: undefined, // Left panel parapet panel type
    rightPanelParapetPanelType: undefined // Right panel parapet panel type
  });

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Safety check - if we're not on this route, navigate away
  useEffect(() => {
    if (location.pathname !== '/voordeur') {
      // Force navigation if somehow we're still mounted on wrong route
      navigate('/configurator-select', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <div className="h-screen w-screen overflow-hidden bg-gray-50">
      {/* Header Bar */}
      <div className="bg-slate-800 text-white px-6 py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={() => {
                navigate('/configurator-select', { replace: false });
                // Force a page reload as fallback
                setTimeout(() => {
                  if (window.location.pathname !== '/configurator-select') {
                    window.location.href = '/configurator-select';
                  }
                }, 100);
              }}
              className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-600 text-sm text-white hover:bg-slate-700 transition-colors z-50 relative"
            >
              <span className="mr-1">←</span>
              {t('configurator.back')}
            </button>
            <div>
              <h1 className="text-2xl font-bold">{t('configurator.frontDoorConfigurator')}</h1>
              <p className="text-sm text-slate-300 mt-0.5">{t('configurator.professionalSalesTool')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setShowSavedQuotes(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('configurator.savedQuotes')}
            </button>
            <button 
              onClick={() => setShowQuote(true)}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('configurator.exportQuote')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Panel - Options */}
        <div className="w-full lg:w-1/3 xl:w-1/4 overflow-hidden">
          <VoordeurOptionsPanel
            config={config}
            onConfigChange={handleConfigChange}
          />
        </div>

        {/* Middle Panel - Drawing Board */}
        <div className="flex-1 p-6 overflow-hidden relative">
          <div ref={drawingBoardRef} className="h-full w-full flex items-center justify-center">
            <DoorDrawingBoard
              width={config.width}
              height={config.height}
              doorStyle={config.doorStyle}
              designType={config.designType}
              doorType={config.doorType}
              handlePosition={config.handlePosition}
              interiorColor={config.interiorColor}
              exteriorColor={config.exteriorColor}
              glassTexture={config.glassTexture}
              parapet={config.parapet}
              overheadPartWindowTypes={config.overheadPartWindowTypes}
              overheadHeight={config.overheadHeight}
              leftPanelWidth={config.leftPanelWidth}
              rightPanelWidth={config.rightPanelWidth}
              leftPanelWindowType={config.leftPanelWindowType}
              rightPanelWindowType={config.rightPanelWindowType}
              onOverheadPartWindowTypeChange={(index, value) => {
                const newTypes = [...(config.overheadPartWindowTypes || ['fixed-sash'])];
                newTypes[index] = value;
                handleConfigChange('overheadPartWindowTypes', newTypes);
              }}
              onOverheadHeightChange={(value) => handleConfigChange('overheadHeight', value)}
              onLeftPanelWidthChange={(value) => handleConfigChange('leftPanelWidth', value)}
              onRightPanelWidthChange={(value) => handleConfigChange('rightPanelWidth', value)}
              onLeftPanelWindowTypeChange={(value) => handleConfigChange('leftPanelWindowType', value)}
              onRightPanelWindowTypeChange={(value) => handleConfigChange('rightPanelWindowType', value)}
              leftPanelParapet={config.leftPanelParapet}
              rightPanelParapet={config.rightPanelParapet}
              leftPanelParapetHeight={config.leftPanelParapetHeight}
              rightPanelParapetHeight={config.rightPanelParapetHeight}
              leftPanelParapetColor={config.leftPanelParapetColor}
              rightPanelParapetColor={config.rightPanelParapetColor}
              leftPanelParapetPanelType={config.leftPanelParapetPanelType}
              rightPanelParapetPanelType={config.rightPanelParapetPanelType}
              onLeftPanelParapetChange={(value) => handleConfigChange('leftPanelParapet', value)}
              onRightPanelParapetChange={(value) => handleConfigChange('rightPanelParapet', value)}
              onLeftPanelParapetHeightChange={(value) => handleConfigChange('leftPanelParapetHeight', value)}
              onRightPanelParapetHeightChange={(value) => handleConfigChange('rightPanelParapetHeight', value)}
              onLeftPanelParapetColorChange={(value) => handleConfigChange('leftPanelParapetColor', value)}
              onRightPanelParapetColorChange={(value) => handleConfigChange('rightPanelParapetColor', value)}
              onLeftPanelParapetPanelTypeChange={(value) => handleConfigChange('leftPanelParapetPanelType', value)}
              onRightPanelParapetPanelTypeChange={(value) => handleConfigChange('rightPanelParapetPanelType', value)}
              onWidthChange={(value) => handleConfigChange('width', value)}
              onHeightChange={(value) => handleConfigChange('height', value)}
            />
          </div>
          
          {/* Toggle Summary Button - Floating on right edge */}
          <button
            onClick={() => setShowSummary(!showSummary)}
            className={`absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-orange-500 hover:bg-orange-600 text-white rounded-l-lg px-3 py-4 shadow-lg transition-all duration-300 flex items-center justify-center ${
              showSummary ? 'opacity-100' : 'opacity-100'
            }`}
            title={showSummary ? 'Hide Summary' : 'Show Summary'}
          >
            <svg
              className={`w-5 h-5 transition-transform duration-300 ${showSummary ? 'rotate-0' : 'rotate-180'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        {/* Right Panel - Summary (Slide in/out) */}
        <div className={`relative overflow-hidden border-l bg-white transition-all duration-300 ease-in-out ${
          showSummary ? 'w-full lg:w-1/4 xl:w-1/5' : 'w-0 border-0'
        }`}>
          <div className={`h-full p-4 transition-opacity duration-300 ${
            showSummary ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}>
            <Summary
              config={config}
              pricing={PRICING}
              profileSystems={[]}
              glassTextures={glassTextures}
              glazingTypes={glazingTypes}
              colors={colors}
              raamtypes={[]}
              hardware={[]}
              accessories={[]}
              woodJointOptions={[]}
              partsOptions={[]}
              overheadOptions={[]}
              drawingBoardRef={drawingBoardRef}
              pageType="voordeur"
              onConfigChange={handleConfigChange}
            />
          </div>
        </div>
      </div>
      
      {/* Quote Modal */}
      <Quote
        key={`quote-${config.quantity}`}
        open={showQuote}
        onOpenChange={setShowQuote}
        config={config}
        pricing={PRICING}
        profileSystems={[]}
        glassTextures={glassTextures}
        glazingTypes={glazingTypes}
        colors={colors}
        raamtypes={[]}
        hardware={[]}
        accessories={[]}
        woodJointOptions={[]}
        partsOptions={[]}
        overheadOptions={[]}
        drawingBoardRef={drawingBoardRef}
        actionMarginTopRem={10.5}
      />
      
      {/* Saved Quotes Modal */}
      <SavedQuotes
        open={showSavedQuotes}
        onOpenChange={setShowSavedQuotes}
      />
    </div>
  );
};

export default Voordeur;

